import {
  ConfirmationPattern,
  DevicePoolIdentifier,
  DeviceTrigger,
  EventType,
  ValueExtender,
  Zone,
  ZonePayload,
  ZoneStrategy,
} from '@ekkogmbh/apisdk';
import { Settings, Warning } from '@mui/icons-material';
import { ListItemText, MenuItem, Stack, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { StyledCheckbox } from 'src/Common/Components/Forms/StyledCheckbox';
import { StyledFormHeader } from 'src/Common/Components/Forms/StyledFormHeader';
import { EventTriggerForm } from 'src/EventRuleManagement/Components/EventTriggerForm';
import { CoordinateInput } from '../../Common/Components/CoordinateInput';
import { StyledSelectField } from '../../Common/Components/Forms/StyledSelectField';
import { StyledTextField } from '../../Common/Components/Forms/StyledTextField';
import { ConfirmStrategyConfigMap, isMergeConfigValid, MergeStrategiesConfigMap } from '../Helper/Strategies';
import { DevicePoolPicker } from './DevicePoolPicker';
import { NotificationStrategyForm } from '../../Common/Components/Forms/NotificationStrategyForm';
import { ContentPanel, StepDefinition, usePanel } from 'src/Common/Components/ContentControl/ContentPanel';
import NamedCoordinateDescription from 'src/Common/Components/Stepper/NameCoordinateDescription';
import { CenteredColumn, ReactivePanelColumn, ReactiveRow } from 'src/Common/Components/Stacks';
import ObjectField from 'src/Common/Components/DataTable/ObjectField';
import useEventTrigger from 'src/EventRuleManagement/Providers/EventTriggerProvider';
import useEkko from 'src/Common/Providers/EkkoProvider';
import useNotificationStrategy from '../Providers/NotificationStrategyProvider';
import ZoneStrategyFormSelect from './ZoneStrategyFormSelect';
import ValueExtendersInput from './ValueExtendersInput';
import StyledFormAccordion from 'src/Common/Components/Forms/StyledFormAccordion';

interface SideConfig {
  pool?: DevicePoolIdentifier;
  extenders?: ValueExtender[];
  pattern: ConfirmationPattern;
  isOptional: boolean;
}
interface Props {
  zone?: Zone;
  closeHandler: () => void;
  saveHandler: (savableZone: ZonePayload, allowOverwrite: boolean) => Promise<void>;
}

export const ZonePanel = ({ zone, closeHandler, saveHandler }: Props): React.JSX.Element => {
  const { config } = useEkko();
  const { currentStep, enableNext, enableSave } = usePanel();

  const { notificationStrategy, setStrategies: setNotificationStrategy } = useNotificationStrategy();
  const [isSideConfigOpen, setSideConfigOpen] = useState<boolean>(false);

  const { eventTrigger, setTriggers: setConfirmationTrigger } = useEventTrigger();
  const [name, setName] = useState<string>('');
  const [coordinate, setCoordinate] = useState<string>('');
  const [confirmationStrategy, setConfirmationStrategy] = useState<ZoneStrategy>({
    name: 'strict',
    configuration: {},
  });
  const [mergeStrategy, setMergeStrategy] = useState<ZoneStrategy>({
    name: 'append',
    configuration: {},
  });
  const [sortSteps, setSortSteps] = useState<string>('no');
  const [maxParallelOpenOrders, setMaxParallelOpenOrders] = useState<number>(0);
  const [isAutoConclude, setAutoConclude] = useState<boolean>(true);
  const [isAutoDelete, setAutoDelete] = useState<boolean>(false);
  const [confirmationTopic, setConfirmationTopic] = useState<string>(config.buttonConfirmEventTopics[0] ?? '');
  const [sourceConfig, setSourceConfig] = useState<SideConfig>({
    pattern: ConfirmationPattern.DEFAULT,
    isOptional: false,
  });
  const [destinationConfig, setDestinationConfig] = useState<SideConfig>({
    pattern: ConfirmationPattern.DEFAULT,
    isOptional: false,
  });

  const zonePayload = useMemo(() => {
    if ([name, coordinate].includes('')) {
      return undefined;
    }

    return {
      name,
      coordinate,
      mergeStrategy,
      confirmationStrategy,
      sortSteps,
      maxParallelOpenOrders,
      autoConclude: isAutoConclude,
      autoDelete: isAutoDelete,
      notificationStrategy,
      buttonConfirmationEventTopic: confirmationTopic,
      confirmationTrigger: eventTrigger,
      sourcePoolIdentifier: sourceConfig?.pool,
      sourceValueExtenders: sourceConfig?.extenders,
      sourceOptionalConfirmation: sourceConfig.isOptional,
      sourceConfirmationPattern: sourceConfig.pattern,
      destinationPoolIdentifier: destinationConfig?.pool,
      destinationValueExtenders: destinationConfig?.extenders,
      destinationOptionalConfirmation: destinationConfig.isOptional,
      destinationConfirmationPattern: destinationConfig.pattern,
    } as ZonePayload;
  }, [
    name,
    coordinate,
    confirmationStrategy,
    confirmationStrategy.configuration,
    mergeStrategy,
    sortSteps,
    maxParallelOpenOrders,
    isAutoConclude,
    isAutoDelete,
    confirmationTopic,
    sourceConfig,
    destinationConfig,
    eventTrigger,
    notificationStrategy,
  ]);

  const reset = useCallback(() => {
    setName(zone?.name ?? '');
    setCoordinate(zone?.coordinate ?? '');
    setConfirmationStrategy(
      zone?.confirmationStrategy ?? {
        name: 'strict',
        configuration: {},
      },
    );
    setMergeStrategy(
      zone?.mergeStrategy ?? {
        name: 'append',
        configuration: {},
      },
    );
    setSortSteps(zone?.sortSteps ?? 'no');
    setMaxParallelOpenOrders(zone?.maxParallelOpenOrders ?? 0);
    setAutoConclude(zone?.autoConclude ?? true);
    setAutoDelete(zone?.autoDelete ?? false);
    setConfirmationTopic(zone?.buttonConfirmationEventTopic ?? config.buttonConfirmEventTopics[0] ?? '');
    setNotificationStrategy(zone?.notificationStrategy);
    setConfirmationTrigger(
      zone?.confirmationTrigger ?? {
        type: EventType.DEVICE_TRIGGER,
        properties: {
          deviceTriggerType: 'button-pressed',
        },
      },
    );

    if (zone !== undefined) {
      setSourceConfig({
        pool: zone.sourcePool ?? undefined,
        extenders: zone.sourceValueExtenders,
        pattern: zone.sourceConfirmationPattern,
        isOptional: zone.sourceOptionalConfirmation,
      });
      setDestinationConfig({
        pool: zone.destinationPool ?? undefined,
        extenders: zone.destinationValueExtenders,
        pattern: zone.destinationConfirmationPattern,
        isOptional: zone.destinationOptionalConfirmation,
      });
    } else {
      setSourceConfig({
        pattern: ConfirmationPattern.DEFAULT,
        isOptional: false,
      });
      setDestinationConfig({
        pattern: ConfirmationPattern.DEFAULT,
        isOptional: true,
      });
    }
  }, [zone]);

  /** on mount / unmount */
  useEffect(() => {
    if (zone !== undefined) {
      setName(zone.name);
      setCoordinate(zone.coordinate);
      setConfirmationStrategy(zone.confirmationStrategy);
      setMergeStrategy(zone.mergeStrategy);
      setSortSteps(zone.sortSteps);
      setMaxParallelOpenOrders(zone.maxParallelOpenOrders);
      setAutoConclude(zone.autoConclude);
      setAutoDelete(zone.autoDelete);
      setConfirmationTopic(zone.buttonConfirmationEventTopic ?? '');
      setNotificationStrategy(zone.notificationStrategy);
      setConfirmationTrigger(zone.confirmationTrigger);

      setSourceConfig({
        pool: zone.sourcePool ?? undefined,
        extenders: zone.sourceValueExtenders ?? undefined,
        pattern: zone.sourceConfirmationPattern,
        isOptional: zone.sourceOptionalConfirmation,
      });
      setDestinationConfig({
        pool: zone.destinationPool ?? undefined,
        extenders: zone.destinationValueExtenders,
        pattern: zone.destinationConfirmationPattern,
        isOptional: zone.destinationOptionalConfirmation,
      });
    } else {
      setConfirmationTrigger({
        type: EventType.DEVICE_TRIGGER,
        properties: {
          deviceTriggerType: 'button-pressed',
        },
      } as DeviceTrigger);
    }
  }, [zone]);

  useEffect(() => {
    reset();
  }, [reset]);

  /** panel button enablement */
  useEffect(() => {
    switch (currentStep) {
      case 0: {
        enableNext(name !== '' && coordinate !== '');
        break;
      }
      case 2: {
        enableNext(mergeStrategy !== undefined && isMergeConfigValid(mergeStrategy));
        break;
      }
      default:
        enableNext(true);
    }
    enableSave(zonePayload !== undefined);
  }, [currentStep, name, coordinate, mergeStrategy, zonePayload]);

  const onSave = useCallback(async () => {
    if (zonePayload === undefined) {
      return;
    }

    const overwrite = zone !== undefined;
    await saveHandler(zonePayload, overwrite);
    closeHandler();
  }, [saveHandler, closeHandler, zonePayload]);

  const orderManagementSection = useMemo(() => {
    const sortStepsOptions = [
      <MenuItem key={`sort-steps-option-on`} value={'no'}>
        <ListItemText primary={'NO'} />
      </MenuItem>,
      <MenuItem key={`sort-steps-option-bysource`} value={'by-source'}>
        <ListItemText primary={'BY-SOURCE'} />
      </MenuItem>,
      <MenuItem key={`sort-steps-option-bydestination`} value={'by-destination'}>
        <ListItemText primary={'BY-DESTINATION'} />
      </MenuItem>,
    ];

    return (
      <StyledFormAccordion label={'Order Management'}>
        <CenteredColumn width={'100%'}>
          <StyledSelectField
            value={sortSteps ?? 'no'}
            label={'Sort Steps'}
            onChange={(e) => {
              setSortSteps(String(e.target.value));
            }}
          >
            {sortStepsOptions}
          </StyledSelectField>
          <StyledTextField
            type={'number'}
            label={'Max Parallel Open Orders'}
            value={maxParallelOpenOrders}
            onChange={(e) => {
              const inputNumber = parseInt(e.target.value);
              const value = isNaN(inputNumber) ? 0 : Math.max(0, inputNumber);
              setMaxParallelOpenOrders(value);
            }}
            tooltip={'set to 0 for unlimited orders'}
          />
          <StyledCheckbox
            label={'Automatically Conclude Orders'}
            value={isAutoConclude}
            onChange={(e) => {
              setAutoConclude((e.target as HTMLInputElement).checked);
            }}
            tooltip={'Aborting an order or completing all setps flags the order as concluded'}
          />
          <StyledCheckbox
            label={'Automatically Delete Concluded Orders'}
            value={isAutoDelete}
            onChange={(e) => {
              setAutoDelete((e.target as HTMLInputElement).checked);
            }}
            tooltip={'Delete orders when they are flagged as concluded'}
          />
        </CenteredColumn>
      </StyledFormAccordion>
    );
  }, [sortSteps, isAutoConclude, isAutoDelete, maxParallelOpenOrders]);

  const confirmationTriggerSection = useMemo(() => {
    const buttonConfirmationEventTopicOptions = config.buttonConfirmEventTopics.map((topic: string) => (
      <MenuItem key={topic} value={topic}>
        <ListItemText primary={topic} />
      </MenuItem>
    ));
    buttonConfirmationEventTopicOptions.unshift(
      <MenuItem key={'disable'} value={''}>
        <ListItemText primary={'disable'} />
      </MenuItem>,
    );
    return (
      <StyledFormAccordion label={'Confirmation Events'}>
        <StyledSelectField
          value={confirmationTopic}
          label={'Confirmation Event Topic'}
          onChange={(e) => {
            setConfirmationTopic(String(e.target.value));
          }}
          tooltip={
            <Stack direction={'column'} spacing={2} sx={{ px: 2 }}>
              <Stack direction={'row'} spacing={1}>
                <Warning color={'warning'} />
                <Typography>Required for confirmation events</Typography>
              </Stack>
              <Typography>Allows redirecting events to custom message queues.</Typography>
              <Typography>If not set, confirmations must be done via API commands.</Typography>
            </Stack>
          }
        >
          {buttonConfirmationEventTopicOptions}
        </StyledSelectField>
        <EventTriggerForm coordinate={coordinate} />
      </StyledFormAccordion>
    );
  }, [config, confirmationTopic]);

  const namingStep: StepDefinition = useMemo(() => {
    const description = (
      <Stack direction={'column'}>
        <ObjectField
          direction={'column'}
          fields={[
            {
              key: 'Identifier',
              value: <NamedCoordinateDescription name={name} coordinate={coordinate} />,
            },
            {
              key: 'Max. Orders',
              value: [0, undefined].includes(maxParallelOpenOrders) ? '∞' : maxParallelOpenOrders!,
            },
            {
              key: 'Auto Conclude',
              value: isAutoConclude ?? false,
            },
            {
              key: 'Auto Delete',
              value: isAutoDelete ?? false,
            },
          ]}
        />
      </Stack>
    );

    return {
      title: 'Zone',
      description,
      contentCallback: () => (
        <ReactivePanelColumn lgWidth={'66%'}>
          <StyledFormHeader label={'Identifier'} />
          <StyledTextField
            type={'text'}
            label={'Name'}
            value={name}
            disabled={zone !== undefined}
            onChange={(e) => {
              setName(String(e.target.value));
            }}
          />
          <CoordinateInput
            value={coordinate}
            disabled={zone !== undefined}
            onChange={(coordinate) => {
              setCoordinate(coordinate);
            }}
            trailingDelimiter={false}
          />
          <StyledFormHeader label={'Options'} />
          {orderManagementSection}
          {confirmationTriggerSection}
        </ReactivePanelColumn>
      ),
    };
  }, [name, coordinate, orderManagementSection, confirmationTriggerSection]);

  const sidesStep: StepDefinition = useMemo(() => {
    const confirmationPatternOptions = Object.values(ConfirmationPattern).map((pattern: string) => (
      <MenuItem key={`confirmation-mode-option-${pattern}`} value={pattern}>
        <ListItemText primary={pattern.toUpperCase().replace('_', '-')} />
      </MenuItem>
    ));

    const description = (
      <ObjectField
        headlined
        direction={'row'}
        keyColor={'secondary'}
        fields={[
          {
            key: 'Source',
            value: (
              <ObjectField
                direction={'column'}
                fields={[
                  {
                    key: 'Pool',
                    value: sourceConfig.pool?.name ?? '-',
                  },
                  {
                    key: 'Pattern',
                    value: sourceConfig.pattern ?? '-',
                  },
                  {
                    key: 'Optional',
                    value: sourceConfig.isOptional ?? false,
                  },
                ]}
              />
            ),
          },
          {
            key: 'Destination',
            value: (
              <ObjectField
                direction={'column'}
                fields={[
                  {
                    key: 'Pool',
                    value: destinationConfig.pool?.name ?? '-',
                  },
                  {
                    key: 'Pattern',
                    value: destinationConfig.pattern ?? '-',
                  },
                  {
                    key: 'Optional',
                    value: destinationConfig.isOptional ?? false,
                  },
                ]}
              />
            ),
          },
        ]}
      />
    );

    return {
      title: 'Sides Configuration',
      description,
      contentCallback: () => (
        <ReactiveRow>
          <CenteredColumn width={'100%'}>
            <StyledFormHeader label={'Source'} />
            <DevicePoolPicker
              label={'Device Pool'}
              optional
              coordinate={coordinate}
              selected={sourceConfig.pool}
              onChange={(newSelected) => setSourceConfig({ ...sourceConfig, pool: newSelected })}
            />
            <Stack direction={'row'}>
              <StyledSelectField
                value={sourceConfig.pattern ?? ConfirmationPattern.DEFAULT}
                label={'Confirmation Pattern'}
                onChange={(e) => {
                  setSourceConfig({ ...sourceConfig, pattern: e.target.value as ConfirmationPattern });
                }}
              >
                {confirmationPatternOptions}
              </StyledSelectField>
              <StyledCheckbox
                label={'Optional Confirmations'}
                value={sourceConfig.isOptional}
                onChange={() => {
                  setSourceConfig({ ...sourceConfig, isOptional: !sourceConfig.isOptional });
                }}
              />
            </Stack>
            <ValueExtendersInput
              label={'Value Extenders'}
              extenders={sourceConfig.extenders ?? []}
              onChange={(value) => {
                setSourceConfig({ ...sourceConfig, extenders: value });
              }}
            />
          </CenteredColumn>
          <CenteredColumn width={'100%'}>
            <StyledFormHeader label={'Destination'} />
            <DevicePoolPicker
              label={'Device Pool'}
              optional
              coordinate={coordinate}
              selected={destinationConfig.pool}
              onChange={(newSelected) => setDestinationConfig({ ...destinationConfig, pool: newSelected })}
            />
            <Stack direction={'row'}>
              <StyledSelectField
                value={destinationConfig.pattern ?? ConfirmationPattern.DEFAULT}
                label={'Confirmation Pattern'}
                onChange={(e) => {
                  setDestinationConfig({ ...destinationConfig, pattern: e.target.value as ConfirmationPattern });
                }}
              >
                {confirmationPatternOptions}
              </StyledSelectField>
              <StyledCheckbox
                label={'Optional Confirmations'}
                value={destinationConfig.isOptional}
                onChange={() => {
                  setDestinationConfig({ ...destinationConfig, isOptional: !destinationConfig.isOptional });
                }}
              />
            </Stack>
            <ValueExtendersInput
              label={'Value Extenders'}
              extenders={destinationConfig.extenders ?? []}
              onChange={(value) => {
                setDestinationConfig({ ...destinationConfig, extenders: value });
              }}
            />
          </CenteredColumn>
        </ReactiveRow>
      ),
    };
  }, [sourceConfig, destinationConfig]);

  const strategiesStep: StepDefinition = useMemo(() => {
    const description = (
      <ObjectField
        direction={'column'}
        fields={[
          {
            key: 'Confirmation',
            value: confirmationStrategy.name,
          },
          {
            key: 'Notification',
            value: notificationStrategy?.name ?? '',
          },
          {
            key: 'Merge',
            value: mergeStrategy.name,
          },
        ]}
      />
    );

    return {
      title: 'Strategies',
      description,
      contentCallback: () => (
        <ReactivePanelColumn lgWidth={'66%'}>
          <ZoneStrategyFormSelect
            label={'Confirmation'}
            strategy={confirmationStrategy}
            configMap={ConfirmStrategyConfigMap}
            defaultStrategy={{ name: 'strict', configuration: {} }}
            onChange={(strategy) => setConfirmationStrategy(strategy!)}
          />
          <ZoneStrategyFormSelect
            label={'Merge'}
            strategy={mergeStrategy}
            configMap={MergeStrategiesConfigMap}
            defaultStrategy={{ name: 'append', configuration: {} }}
            onChange={(strategy) => setMergeStrategy(strategy!)}
          />
          <StyledTextField
            label={'Notification'}
            value={notificationStrategy?.name ?? ''}
            disabled={true}
            action={{
              onClick: () => {
                setSideConfigOpen(!isSideConfigOpen);
              },
              icon: Settings,
            }}
            collapsible={{
              content: <NotificationStrategyForm showDisablePrevious={false} showIgnoreQueue={false} />,
              open: isSideConfigOpen,
            }}
          />
        </ReactivePanelColumn>
      ),
    };
  }, [notificationStrategy, mergeStrategy, confirmationStrategy, isSideConfigOpen]);

  return (
    <ContentPanel
      steps={[namingStep, sidesStep, strategiesStep]}
      onSave={onSave}
      onCancel={closeHandler}
      onReset={reset}
    />
  );
};
